import { designVariables } from "@studio3t/design-variables";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

export const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

export const Section = styled.section`
  margin-bottom: 20px;
`;

// Height of a "full" Logs card — 5 log rows + the View-all button — so the empty
// states of the tabbed sections (Workers / Checkpoints / Logs) all reserve that
// same height and the card doesn't jump when switching tabs. Built from the same
// tokens the logs card uses: vertical padding (×2) + 5 rows + 5 row gaps +
// view-all padding-top + button height.
const EMPTY_SEMANTIC = designVariables.ui.semantic;
export const FULL_LOGS_HEIGHT =
  EMPTY_SEMANTIC.spacings * 2 +
  designVariables.ui.badge.height * 5 +
  EMPTY_SEMANTIC.spacings * 5 +
  EMPTY_SEMANTIC.spacings +
  designVariables.ui.button.s.size;

// Shared empty/placeholder state for the tabbed sections: centered both ways in
// a full-logs-card-tall box, with a larger label than the inline empty rows.
export const TabEmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: ${FULL_LOGS_HEIGHT}px;
  padding: 0 ${EMPTY_SEMANTIC.spacingl}px;
  font-size: ${designVariables.type.singleLine.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextDisabled};
`;

// Container for the page-level loading skeleton: spaces the placeholder
// blocks like the real header + sections stack.
export const PageSkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: ${(props) => props.theme.semanticTextHeading};
`;

export const Card = styled.div`
  background: ${(props) => props.theme.cards1Background};
  border: 1px solid ${(props) => props.theme.cards1Border};
  border-radius: 6px;
  padding: 16px;
`;

export const PipelineViz = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  flex-wrap: wrap;
`;

export const SinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PipelineStageLink = styled(Link)<{
  $type: "source" | "transform" | "sink";
  $unresolved?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 24px;
  border-radius: 8px;
  min-width: 160px;
  text-decoration: none;
  transition:
    transform 0.1s ease,
    box-shadow 0.1s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  background: ${(props) => {
    if (props.$unresolved) return "rgba(245, 158, 11, 0.08)";
    switch (props.$type) {
      case "source":
        return "rgba(239, 68, 68, 0.1)";
      case "transform":
        return "rgba(168, 85, 247, 0.1)";
      case "sink":
        return "rgba(34, 197, 94, 0.1)";
    }
  }};
  border: ${(props) => {
    if (props.$unresolved) return "1.5px dashed rgba(245, 158, 11, 0.6)";
    switch (props.$type) {
      case "source":
        return "1px solid rgba(239, 68, 68, 0.3)";
      case "transform":
        return "1px solid rgba(168, 85, 247, 0.3)";
      case "sink":
        return "1px solid rgba(34, 197, 94, 0.3)";
    }
  }};
`;

export const UnresolvedBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
  margin-top: 6px;
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
`;

export const StageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${(props) => props.theme.semanticTextLight};
  margin-bottom: 6px;
`;

export const StageService = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.theme.semanticTextHeading};
`;

export const StageResource = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.semanticTextLight};
`;

export const StageBadges = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;
  flex-wrap: wrap;
  justify-content: center;
`;

export const StageBadge = styled.span<{ $type: "schema" | "encoding" }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${(props) =>
    props.$type === "schema"
      ? "rgba(59, 130, 246, 0.15)"
      : "rgba(107, 114, 128, 0.15)"};
  color: ${(props) =>
    props.$type === "schema" ? "#3b82f6" : props.theme.semanticTextLight};
  font-weight: 500;
`;

export const Arrow = styled.div`
  font-size: 20px;
  color: ${(props) => props.theme.semanticTextDisabled};
`;

export const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  /* Grid items default to min-width:auto and refuse to shrink below their
     content, so a wide card (the 340px metric cells) would push the whole row
     past the viewport. min-width:0 lets each card shrink to its column so the
     overflow stays inside the card (MetricGrid / ConfigBody scroll on X). */
  & > * {
    min-width: 0;
  }
`;

export const ConfigList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px 16px;
`;

export const ConfigLabel = styled.dt`
  font-size: 12px;
  color: ${(props) => props.theme.semanticTextLight};
`;

export const ConfigValue = styled.dd`
  font-size: 12px;
  color: ${(props) => props.theme.semanticTextBody};
  margin: 0;
  word-break: break-all;
`;

// The card + its config-body row primitives (ConfigBody/ConfigItem/ConfigKey/
// ConfigVal/ConfigBadges/ConfigActions) now live in the shared
// @/components/SectionCard, reused by the single-pipeline and single-service
// pages. The dl-based ConfigList above is still used by the Schemas section.

export const LogsContainer = styled.div`
  background: ${(props) => props.theme.tableTheadCellBackground};
  border-radius: 4px;
  padding: 12px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  font-size: 11px;
  line-height: 1.6;
  max-height: 250px;
  overflow: auto;
`;

export const ViewAllLink = styled(Link)`
  font-size: 12px;
  color: ${(props) => props.theme.linkColor};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const LogsEmptyState = styled.div`
  text-align: center;
  padding: 24px;
  color: ${(props) => props.theme.semanticTextDisabled};
  font-size: 12px;
`;

export const LogLine = styled.div`
  color: ${(props) => props.theme.semanticTextBody};

  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }
`;

export const LogTimestamp = styled.span`
  color: ${(props) => props.theme.semanticTextDisabled};
  margin-right: 12px;
`;

export const LogLevel = styled.span<{ $level: string }>`
  color: ${(props) => {
    switch (props.$level) {
      case "ERROR":
        return "#ff6b6b";
      case "WARN":
        return "#ffd43b";
      default:
        return "#00ed64";
    }
  }};
  margin-right: 12px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: ${(props) => props.theme.semanticTextDisabled};
`;

// React Flow canvas wrapper. React Flow needs an explicitly sized container;
// fitView then frames the graph regardless of this fixed height.
export const FlowCanvas = styled.div`
  width: 100%;
  height: 440px;

  /* The node wrapper is non-interactive (read-only canvas); re-enable pointer
     events on the stage link so clicking a node still navigates to its service. */
  .react-flow__node a {
    pointer-events: auto;
  }

  .react-flow__attribution {
    font-size: 9px;
  }
`;

// --- Pipeline stage card (Figma "Bridge_PipelineStage") ----------------------
// All spacings/corners/colours come from design-variables.
const CARD_WIDTH = 280; // px — fixed card size from the design
// Figma Bridge_PipelineStage (13984-207179): the 124px frame includes the
// schema badge slot, reserved whether or not the stage refs a schema.
const CARD_HEIGHT = 124;
const { spacings: CARD_GAP, spacingm: PAD_M } = designVariables.ui.semantic; // 10 / 14
const GAP_S = designVariables.primitives.spacings.s; // 4
const CARD_CORNER = designVariables.ui.cards.corner; // 6
const RAINBOW = designVariables.primitives.rainbow.alpha;

const STAGE_ACCENT = {
  source: RAINBOW.yellow,
  transform: RAINBOW.orange,
  sink: RAINBOW.turquoise,
} as const;

type StageCardType = keyof typeof STAGE_ACCENT;

interface StageCardProps {
  $type: StageCardType;
  $unresolved?: boolean;
  $backed?: boolean;
}

const stageCardCss = css<StageCardProps>`
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  height: ${CARD_HEIGHT}px;
  border-radius: ${CARD_CORNER}px;
  overflow: hidden;
  text-decoration: none;
  /* It's an <a>; without this the default green link colour bleeds into any
     text that doesn't set its own (e.g. the encoding badge). */
  color: ${(p) => p.theme.semanticTextBody};
  /* Cards on the dotted canvas get an opaque base so the dots don't show
     through the semi-transparent fill; grouped cards sit on the grey box. */
  background: ${(p) =>
    p.$backed
      ? `linear-gradient(${STAGE_ACCENT[p.$type]}, ${STAGE_ACCENT[p.$type]}), ${p.theme.cards1Background}`
      : STAGE_ACCENT[p.$type]};
  border: ${(p) =>
    p.$unresolved
      ? "1px dashed rgba(245, 158, 11, 0.7)"
      : `1px solid ${STAGE_ACCENT[p.$type]}`};
`;

export const StageCardLink = styled(Link)<StageCardProps>`
  ${stageCardCss}
  transition: box-shadow 0.1s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
`;

// The same card with nowhere to go: a stage whose service doesn't exist
// has no service page, so the card is a plain box (no hover lift, default
// cursor) and the tooltip says why.
export const StageCardBox = styled.div<StageCardProps>`
  ${stageCardCss}
  cursor: default;
`;

// Figma 13984-207179: a 40px header strip (10px vertical padding around
// the 20px content row) over the body.
export const StageHeader = styled.div<{ $type: StageCardType }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GAP_S}px;
  padding: ${CARD_GAP}px ${PAD_M}px;
  width: 100%;
  box-sizing: border-box;
  /* Inset hairline instead of a border so the strip stays exactly 40px
     (Figma draws the divider inside the 40px header frame). */
  box-shadow: inset 0 -1px 0 ${(p) => STAGE_ACCENT[p.$type]};
`;

export const StageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${GAP_S}px;
  min-width: 0;
`;

export const StageLabelText = styled.span`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1.2;
  white-space: nowrap;
  color: ${(p) => p.theme.semanticTextBody};
`;

// Body per the same frame: 10px vertical padding, 5px between the rows
// and the schema badge slot.
export const StageBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: ${CARD_GAP}px ${PAD_M}px;
  width: 100%;
  box-sizing: border-box;
`;

export const StageRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${GAP_S}px;
  width: 100%;
  min-width: 0;
`;

export const StageRowText = styled.span<{ $regular?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: ${(p) => (p.$regular ? 400 : 500)};
  /* Figma rows are 17px tall (Single-line/Default at 1.2) — 1.4 would
     overflow the fixed 124px card and eat the bottom padding. */
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextBody};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// --- Flow card header (Figma "Card/header" 13984-224404) ---------------------
// The design-system card header: a 50px row with spacingm horizontal padding,
// H6 title on the left, small buttons on the right — the same proportions as
// the single-service section cards.
const BTN = designVariables.ui.button;
const HEADING = designVariables.type.heading;

export const FlowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${PAD_M}px;
  min-height: 50px;
  padding: 0 ${PAD_M}px;
  box-sizing: border-box;
  background: ${(p) => p.theme.cards1Background};
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
  border-top-left-radius: ${CARD_CORNER}px;
  border-top-right-radius: ${CARD_CORNER}px;
`;

export const FlowHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${CARD_GAP}px;
  min-width: 0;
`;

export const FlowHeaderTitle = styled.h2`
  margin: 0;
  min-width: 0;
  font-size: ${HEADING.h6.fontSize}px;
  font-weight: ${HEADING.h6.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FlowHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${CARD_GAP}px;
  flex-shrink: 0;
`;

export const SegmentedGroup = styled.div`
  display: inline-flex;
  align-items: center;
`;

export const SegmentedButton = styled.button<{
  $active?: boolean;
  $side: "left" | "right";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${BTN.s.size}px;
  padding: 0 ${BTN.s.paddingw}px;
  border: 1px solid ${(p) => p.theme.buttonPrimaryDefaultBorder};
  background: ${(p) =>
    p.$active
      ? p.theme.buttonPrimaryActiveBackground
      : p.theme.buttonPrimaryDefaultBackground};
  cursor: pointer;
  border-radius: ${(p) =>
    p.$side === "left"
      ? `${BTN.corner}px 0 0 ${BTN.corner}px`
      : `0 ${BTN.corner}px ${BTN.corner}px 0`};
  margin-left: ${(p) => (p.$side === "right" ? "-1px" : "0")};
  z-index: ${(p) => (p.$side === "left" ? 2 : 1)};

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.buttonPrimaryHoverBackground};
    border-color: ${(p) => p.theme.buttonPrimaryHoverBorder};
    z-index: 3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  /* Keyboard focus (TTTL-186): the pair carries the primary border and
     an active primary fill, which an inset ring would merge into — its
     ring sits outside, past a gap, and the focused half rises above its
     sibling so the ring is whole. */
  --focus-ring-offset: 2px;
  &:focus-visible {
    z-index: 4;
  }
`;

export const StudioButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${BTN.gap}px;
  height: ${BTN.s.size}px;
  padding: 0 ${BTN.s.paddingw}px;
  border-radius: ${BTN.corner}px;
  border: 1px solid ${(p) => p.theme.buttonSecondaryDefaultBorder};
  background: ${(p) => p.theme.buttonSecondaryDefaultBackground};
  color: ${(p) => p.theme.buttonSecondaryDefaultText};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
    border-color: ${(p) => p.theme.buttonSecondaryHoverBorder};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
